import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  alpha,
  Card,
  Divider,
} from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SavingsIcon from "@mui/icons-material/Savings";
import PrintIcon from "@mui/icons-material/Print";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import LanguageIcon from "@mui/icons-material/Language";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import {
  CATEGORICAL_COLORS,
  CHART_INK,
  getDefaultMonthRange,
  monthInputToRange,
} from "../../components/StatisticsDashboard/chartUtils";
import SavingsChart from "../../components/StatisticsDashboard/SavingsChart";
import StatusBreakdownChart from "../../components/StatisticsDashboard/StatusBreakdownChart";
import DashboardCard from "../../components/StatisticsDashboard/DashboardCard";
import MonthRangeFilter from "../../components/StatisticsDashboard/MonthRangeFilter";
import NotFoundPage from "../ErrorPage";

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || "").trim();
const PAGE_COLOR = "#b45309";
const TREND_CHART_COLOR = CATEGORICAL_COLORS[1];
const LABEL_PRINTS_CHART_COLOR = CATEGORICAL_COLORS[3];
// Same green used for the "savings" card on the approveLines/salesPromotions
// dashboards (CATEGORICAL_COLORS[2]) — keeps the "time saved" concept
// visually consistent across all three, instead of the page's own orange.
const SAVINGS_CHART_COLOR = CATEGORICAL_COLORS[2];

// Entry to /EldanDashboard only requires the "ManagersDashboardPage" role
// permission (checked by ProtectedRoute). Whether this specific sub-dashboard
// is enabled is a separate, narrower grant living in managerDashboardPermissionModel
const ELDAN_DASHBOARD_KEY = "eldandashboard";

const SENT_TO_PRIORITY_STATUS = "send to priorty";
const SENT_TO_PRIORITY_LABEL = "סה״כ קריאות שנשלחו לפריוריטי עד היום";

const ELDAN_OPEN_METHOD_GROUPS = [
  { key: "qr", label: "סריקת QR", color: TREND_CHART_COLOR, icon: QrCodeScannerIcon },
  { key: "site", label: "אתר", color: CATEGORICAL_COLORS[0], icon: LanguageIcon },
];

// Recharts renders its Y-axis category labels as plain SVG <text>, and under
// this app's RTL theme the browser reinterprets `text-anchor="end"` relative
// to the surrounding bidi context — instead of ending at the axis line and
// growing left (into the reserved label strip), the text starts at the axis
// line and grows right, straight over the bar. A wrapping `direction: ltr`
// isn't enough to stop that reinterpretation, so this tick renders its own
// <text> with an explicit bidi override, forcing the anchor to behave the
// same regardless of the page's direction.
const UsernameAxisTick = ({ x, y, payload }) => (
  <text
    x={x}
    y={y}
    dy={4}
    textAnchor="end"
    style={{ direction: "ltr", unicodeBidi: "bidi-override" }}
    fontSize={11}
    fill={CHART_INK.muted}
  >
    {payload.value}
  </text>
);

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card sx={{ p: 2.5, borderRadius: 3, display: "flex", alignItems: "center", gap: 2, height: "100%" }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        bgcolor: alpha(color, 0.12),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color, fontSize: 26 }} />
    </Box>
    <Box>
      <Typography variant="h5" fontWeight={700}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  </Card>
);

const { fromMonthInput: DEFAULT_FROM, toMonthInput: DEFAULT_TO } = getDefaultMonthRange();

const EldanDashboardPage = () => {
  // "checking" | "granted" | "denied"
  const [dashboardAccess, setDashboardAccess] = useState("checking");
  const [fromMonth, setFromMonth] = useState(DEFAULT_FROM);
  const [toMonth, setToMonth] = useState(DEFAULT_TO);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [labelPrintsPerUser, setLabelPrintsPerUser] = useState([]);
  const [monthlyTicketsStats, setMonthlyTicketsStats] = useState([]);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [loadingLabelPrints, setLoadingLabelPrints] = useState(true);
  const [loadingMonthlyStats, setLoadingMonthlyStats] = useState(true);
  const [error, setError] = useState(null);

  const [minutesSavedPerTicket, setMinutesSavedPerTicket] = useState(15);
  const [minutesInput, setMinutesInput] = useState('15');
  const [savingMinutes, setSavingMinutes] = useState(false);

  const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });
  const { execute: fetchWrite } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.write });

  useEffect(() => {
    let cancelled = false;

    // A definitive 403 means the backend evaluated access and said no.
    // Anything else (network hiccup, cold-start 5xx, token refresh blip) is a
    // failed check, not a denial — retry once before giving up.
    const checkAccess = async (isRetry = false) => {
      try {
        const result = await fetchRead("GET", `${BASE_URL}/api/managerDashboard/myAccess`);
        if (cancelled) return;
        const allowed = !!result?.isAdmin || !!result?.allowedDashboards?.includes(ELDAN_DASHBOARD_KEY);
        setDashboardAccess(allowed ? "granted" : "denied");
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 403 || isRetry) {
          setDashboardAccess("denied");
          return;
        }
        setTimeout(() => checkAccess(true), 500);
      }
    };

    checkAccess();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No date scoping — this is always the all-time total sent to priority as of now.
  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/summary`);
      setSummary(res);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת נתוני הדשבורד");
    } finally {
      setLoadingSummary(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthRange = useMemo(() => {
    if (!fromMonth || !toMonth) return null;
    return monthInputToRange(fromMonth, toMonth);
  }, [fromMonth, toMonth]);

  const loadTrend = useCallback(async () => {
    if (!monthRange) return;
    setLoadingTrend(true);
    try {
      const params = new URLSearchParams({ startDate: monthRange.startDate, endDate: monthRange.endDate });
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/ticketsTrend?${params.toString()}`);
      setTrend(res.data || []);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת מגמת הקריאות");
    } finally {
      setLoadingTrend(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthRange]);

  // Successful prints only, scoped to the selected date range — reads
  // dymo_eldan_label_logs, a separate collection from the eldan_qrcodes tickets.
  const loadLabelPrintsPerUser = useCallback(async () => {
    if (!monthRange) return;
    setLoadingLabelPrints(true);
    try {
      const params = new URLSearchParams({ startDate: monthRange.startDate, endDate: monthRange.endDate });
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/labelPrintsPerUser?${params.toString()}`);
      setLabelPrintsPerUser(res.data || []);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת נתוני הדפסת המדבקות");
    } finally {
      setLoadingLabelPrints(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthRange]);

  // Monthly ticket counts scoped to the selected date range, powering the
  // "time saved" savings card.
  const loadMonthlyTicketsStats = useCallback(async () => {
    if (!monthRange) return;
    setLoadingMonthlyStats(true);
    try {
      const params = new URLSearchParams({ startDate: monthRange.startDate, endDate: monthRange.endDate });
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/monthly-stats?${params.toString()}`);
      setMonthlyTicketsStats(res.monthlyStats || []);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת נתוני החיסכון");
    } finally {
      setLoadingMonthlyStats(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthRange]);

  const loadSettings = useCallback(async () => {
    try {
      const res = await fetchRead("GET", `${BASE_URL}/api/eldanDashboard/settings`);
      const value = res.settings?.minutesSavedPerTicket ?? 15;
      setMinutesSavedPerTicket(value);
      setMinutesInput(String(value));
    } catch (err) {
      console.error("Error loading eldan-dashboard stats settings:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadTrend(); }, [loadTrend]);
  useEffect(() => { loadLabelPrintsPerUser(); }, [loadLabelPrintsPerUser]);
  useEffect(() => { loadMonthlyTicketsStats(); }, [loadMonthlyTicketsStats]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const sentToPriorityCount = useMemo(
    () => (summary?.byStatus || []).find((entry) => entry.status === SENT_TO_PRIORITY_STATUS)?.count ?? 0,
    [summary]
  );

  const handleSaveMinutes = async () => {
    const value = Number(minutesInput);
    if (Number.isNaN(value) || value < 0) return;
    setSavingMinutes(true);
    try {
      const res = await fetchWrite("PUT", `${BASE_URL}/api/eldanDashboard/settings`, { minutesSavedPerTicket: value });
      setMinutesSavedPerTicket(res.settings?.minutesSavedPerTicket ?? value);
    } catch (err) {
      console.error("Error saving eldan-dashboard stats settings:", err);
    } finally {
      setSavingMinutes(false);
    }
  };

  if (dashboardAccess === "checking") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: PAGE_COLOR }} />
      </Box>
    );
  }

  if (dashboardAccess === "denied") {
    return <NotFoundPage />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h5" fontWeight={700}>אלדן - פתיחת קריאות שירות</Typography>

      <MonthRangeFilter
        fromMonth={fromMonth}
        toMonth={toMonth}
        onFromMonthChange={setFromMonth}
        onToMonthChange={setToMonth}
        color={PAGE_COLOR}
      />

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Alert severity="info">שימו לב: הנתונים המוצגים במסך זה זמינים החל מחודש אפריל 2026.</Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={8} md={4}>
          <StatCard
            label={SENT_TO_PRIORITY_LABEL}
            value={loadingSummary ? "—" : sentToPriorityCount}
            icon={CheckCircleOutlineIcon}
            color={CATEGORICAL_COLORS[1]}
          />
        </Grid>
      </Grid>

      <Divider />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <DashboardCard
            title="קריאות שנפתחו לאורך זמן"
            subtitle="לפי אופן פתיחת הקריאה - הסיווג לפי אתר זמין החל מ- 23.08.26"
            icon={TrendingUpIcon}
            color={TREND_CHART_COLOR}
          >
            {(expanded) => (
              loadingTrend ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: TREND_CHART_COLOR }} /></Box>
              ) : (
                <StatusBreakdownChart
                  monthlyData={trend}
                  expanded={expanded}
                  statusGroups={ELDAN_OPEN_METHOD_GROUPS}
                  totalLabel="סה״כ קריאות בטווח שנבחר"
                />
              )
            )}
          </DashboardCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardCard
            title="חיסכון בזמן עבודה"
            subtitle="לפי מספר הקריאות שנפתחו בחודש"
            icon={SavingsIcon}
            color={SAVINGS_CHART_COLOR}
          >
            {(expanded) => (
              loadingMonthlyStats ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: SAVINGS_CHART_COLOR }} /></Box>
              ) : (
                <SavingsChart
                  monthlyData={monthlyTicketsStats}
                  color={SAVINGS_CHART_COLOR}
                  minutesSavedPerUnit={minutesSavedPerTicket}
                  minutesInput={minutesInput}
                  onMinutesInputChange={setMinutesInput}
                  onSaveMinutes={handleSaveMinutes}
                  saving={savingMinutes}
                  expanded={expanded}
                  minutesFieldLabel="דקות שנחסכות לקריאה"
                />
              )
            )}
          </DashboardCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardCard title="הדפסת מדבקות פר משתמש" icon={PrintIcon} color={LABEL_PRINTS_CHART_COLOR}>
            {(expanded) => (
              loadingLabelPrints ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: LABEL_PRINTS_CHART_COLOR }} /></Box>
              ) : labelPrintsPerUser.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>אין נתוני הדפסות מוצלחות</Typography>
              ) : (
                // Recharts isn't RTL-aware — under the app's inherited `direction: rtl`,
                // the browser flips how the Y-axis <text> anchors itself, so the
                // usernames render on top of the bars instead of beside them.
                // Forcing LTR on the chart's own wrapper sidesteps that without
                // affecting the rest of the (RTL) page.
                <Box sx={{ maxHeight: expanded ? 640 : 480, overflowY: "auto", direction: "ltr" }}>
                  <ResponsiveContainer width="100%" height={Math.max(220, labelPrintsPerUser.length * 28)}>
                    <BarChart
                      data={labelPrintsPerUser}
                      layout="vertical"
                      margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                    >
                      <CartesianGrid horizontal={false} stroke={CHART_INK.grid} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={{ stroke: CHART_INK.axis }} tickLine={false} />
                      <YAxis
                        type="category"
                        dataKey="userName"
                        width={160}
                        tick={<UsernameAxisTick />}
                        axisLine={{ stroke: CHART_INK.axis }}
                        tickLine={false}
                      />
                      <Tooltip formatter={(value) => [`${value} מדבקות`, ""]} cursor={{ fill: alpha(LABEL_PRINTS_CHART_COLOR, 0.06) }} />
                      <Bar dataKey="count" name="מדבקות" fill={LABEL_PRINTS_CHART_COLOR} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )
            )}
          </DashboardCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EldanDashboardPage;
